import { arrayOf, shape, string } from "prop-types";
import { useId, useMemo } from "react";

import classnames from "~utils/classnames/classnames";
import isExternalUrl from "~utils/isExternalUrl";

import LinkText from "~modules/UI/LinkText";

import { UiLinkType } from "~types/index";

import classes from "./LinksListGrid.module.scss";

const emptyArray = [];

/**
 * @param {{ externalLink?: string }} link
 * @returns {boolean}
 */
function linkHasInternalUrl(link) {
  // if the property externalLink is not present, it is considered an internal link
  // if the proparty externalLink is set, but it has the same domain as the website, its also considered an internal link
  return (
    !link.externalLink || !isExternalUrl("iamsterdam.com", link.externalLink)
  );
}

/**
 * LinksListGrid component
 *
 * @param {object} props - Component props
 * @param {string} props.title - The title of the block
 * @param {array} props.items - An array of objects, each containing an array of links
 *
 */

const LinksListGrid = ({ title, items = emptyArray }) => {
  const UUID = useId();
  // there should always be a multiple of 4 columns (items) for the grid to work
  const extraColumnsArray = useMemo(() => {
    if (items.length === 4 || items.length % 4 === 0) {
      return [];
    }

    // Create array with unique IDs for each placeholder column
    const columnsNeeded = 4 - (items.length % 4);
    return Array.from({ length: columnsNeeded }, (_, index) => ({
      id: `LinksListGrid-${UUID}-${index}`,
    }));
  }, [items, UUID]);

  return (
    <section className={classes.section}>
      {title && <h2 className={classes.title}>{title}</h2>}
      <div className={classes.lists}>
        {items.map((item) => (
          <div key={item.id} className={classes.list}>
            {item?.title && Boolean(item.links?.length) && (
              <>
                <h3 className={classes.list_title}>{item.title}</h3>
                <ul className={classes.list_links}>
                  {item.links.map((link) => (
                    <li key={link.id}>
                      <LinkText
                        label={link.label}
                        slug={link.internalLink?.[0]?.slug || link.externalLink}
                        params={link.linkParams}
                        className={classnames(
                          classes.link,
                          linkHasInternalUrl(link) && classes.arrow,
                        )}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ))}
        {extraColumnsArray.map((item) => (
          <div key={item.id} className={classes.list} />
        ))}
      </div>
    </section>
  );
};

LinksListGrid.propTypes = {
  title: string,
  items: arrayOf(
    shape({
      id: string.isRequired,
      title: string.isRequired,
      links: arrayOf(shape(UiLinkType)).isRequired,
    }),
  ).isRequired,
};

export default LinksListGrid;
